import { useState, useCallback } from 'react';
import { createApiClient, ApiError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// Initialize API client with empty baseUrl since paths already include /api/v1.
// CRM 订单端点都受 RBAC + 租户隔离 + 高风险客户额外确认保护；typed client 必须
// 通过共享的 tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`（Issue #226）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

function friendlyOrderErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return '会话已失效，请重新登录后再试';
    if (err.status === 403) return '当前角色没有 CRM 订单访问权限';
    return err.message;
  }
  return fallback;
}

// Issue #226 audit fix: every useOrders/useServiceUsers catch must rethrow a
// controlled error that carries only the translated friendly message, never
// the raw backend ApiError. OrderDetail and CreateOrder display `err.message`
// directly via `err instanceof Error ? err.message : ...`, so leaking the raw
// `ApiError` (e.g. 401 "Authentication required") would surface backend text
// in the UI. Wrap once here, throw only this class out of every action path.
class OrderHookError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OrderHookError';
  }
}

// ─── DTO type aliases ────────────────────────────────────────────────────────
export type CustomerOrderDto = ApiComponents['schemas']['CustomerOrderDto'];
export type CreateCustomerOrderDto = ApiComponents['schemas']['CreateCustomerOrderDto'];
export type ServiceUserDto = ApiComponents['schemas']['ServiceUserDto'];
export type CreateServiceUserDto = ApiComponents['schemas']['CreateServiceUserDto'];
export type OrderProjectDto = ApiComponents['schemas']['CrmOrderProjectDto'];
export type CreateOrderProjectDto = ApiComponents['schemas']['CreateCrmOrderProjectDto'];
export type TransitionSalesStageDto = ApiComponents['schemas']['TransitionSalesStageDto'];
export type ForceTransitionSalesStageDto = ApiComponents['schemas']['ForceTransitionSalesStageDto'];
export type ConfirmHighRiskDto = ApiComponents['schemas']['ConfirmHighRiskDto'];
export type CrmSalesStageConfigDto = ApiComponents['schemas']['CrmSalesStageConfigDto'];

// ─── Sales Stage Labels ───────────────────────────────────────────────────────
export const SALES_STAGE_LABELS: Record<string, string> = {
  lead_in: '线索入库',
  link_building: '建立链接',
  trust_building: '信任构建',
  needs_discovery: '需求挖掘',
  proposal_quote: '方案报价',
  marketing: '营销中',
  delivering: '交付中',
  maintaining: '维护中',
  repurchased: '复购',
  referred: '转介绍',
  lost: '已流失',
};

export const SALES_STAGE_LIST = Object.keys(SALES_STAGE_LABELS) as string[];

export const TERMINAL_STAGES = new Set(['repurchased', 'referred', 'lost']);

export const SALES_STAGE_TRANSITIONS: Record<string, string[]> = {
  lead_in: ['link_building', 'lost'],
  link_building: ['trust_building', 'lost'],
  trust_building: ['needs_discovery', 'lost'],
  needs_discovery: ['proposal_quote', 'lost'],
  proposal_quote: ['marketing', 'lost'],
  marketing: ['delivering', 'lost'],
  delivering: ['maintaining', 'lost'],
  maintaining: ['repurchased', 'referred', 'lost'],
  repurchased: [],
  referred: [],
  lost: [],
};

export const SOURCE_TYPE_LABELS: Record<string, string> = {
  natural_visit: '自然到访',
  referral: '转介绍',
  channel_partner: '渠道合作',
  repurchase: '复购',
  marketing_event: '营销活动',
  online_lead: '线上线索',
};

export const ORDER_TYPE_LABELS: Record<string, string> = {
  individual: '个人订单',
  family: '家庭订单',
  group: '团单',
};

// ─── useOrders hook ───────────────────────────────────────────────────────────
export function useOrders() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listOrders = useCallback(async (params?: {
    customerId?: string;
    salesStage?: 'lead_in' | 'link_building' | 'trust_building' | 'needs_discovery' | 'proposal_quote' | 'marketing' | 'delivering' | 'maintaining' | 'repurchased' | 'referred' | 'lost';
    assignedUserId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ data: CustomerOrderDto[]; total: number }> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.GET('/api/v1/crm/orders', {
        params: { query: params },
      });
      if (apiErr) throw new ApiError(apiErr);
      return {
        data: (data?.data ?? []) as CustomerOrderDto[],
        total: data?.total ?? 0,
      };
    } catch (err) {
      const msg = friendlyOrderErrorMessage(err, 'Failed to list orders');
      setError(msg);
      throw new OrderHookError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const getOrder = useCallback(async (id: string): Promise<CustomerOrderDto> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.GET('/api/v1/crm/orders/{id}', {
        params: { path: { id } },
      });
      if (apiErr) throw new ApiError(apiErr);
      return data as CustomerOrderDto;
    } catch (err) {
      const msg = friendlyOrderErrorMessage(err, 'Failed to get order');
      setError(msg);
      throw new OrderHookError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const createOrder = useCallback(async (dto: CreateCustomerOrderDto): Promise<CustomerOrderDto> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.POST('/api/v1/crm/orders', {
        body: dto,
      });
      if (apiErr) throw new ApiError(apiErr);
      return data as CustomerOrderDto;
    } catch (err) {
      const msg = friendlyOrderErrorMessage(err, 'Failed to create order');
      setError(msg);
      throw new OrderHookError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const transitionStage = useCallback(async (dto: TransitionSalesStageDto): Promise<CustomerOrderDto> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.PUT('/api/v1/crm/orders/{id}/stage', {
        params: { path: { id: dto.id } },
        body: dto,
      });
      if (apiErr) throw new ApiError(apiErr);
      return data as CustomerOrderDto;
    } catch (err) {
      const msg = friendlyOrderErrorMessage(err, 'Failed to transition stage');
      setError(msg);
      throw new OrderHookError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const forceTransitionStage = useCallback(async (dto: ForceTransitionSalesStageDto): Promise<CustomerOrderDto> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.PUT('/api/v1/crm/orders/{id}/stage/force', {
        params: { path: { id: dto.id } },
        body: dto,
      });
      if (apiErr) throw new ApiError(apiErr);
      return data as CustomerOrderDto;
    } catch (err) {
      const msg = friendlyOrderErrorMessage(err, 'Failed to force transition stage');
      setError(msg);
      throw new OrderHookError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const confirmHighRisk = useCallback(async (dto: ConfirmHighRiskDto): Promise<CustomerOrderDto> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.PUT('/api/v1/crm/orders/{id}/confirm-high-risk', {
        params: { path: { id: dto.id } },
        body: dto,
      });
      if (apiErr) throw new ApiError(apiErr);
      return data as CustomerOrderDto;
    } catch (err) {
      const msg = friendlyOrderErrorMessage(err, 'Failed to confirm high risk');
      setError(msg);
      throw new OrderHookError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const listStageConfig = useCallback(async (): Promise<CrmSalesStageConfigDto> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.GET('/api/v1/crm/orders/stage-config');
      if (apiErr) throw new ApiError(apiErr);
      return data as CrmSalesStageConfigDto;
    } catch (err) {
      const msg = friendlyOrderErrorMessage(err, 'Failed to list CRM stage config');
      setError(msg);
      throw new OrderHookError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    listOrders,
    getOrder,
    createOrder,
    transitionStage,
    forceTransitionStage,
    confirmHighRisk,
    listStageConfig,
  };
}

// ─── useServiceUsers hook ─────────────────────────────────────────────────────
export function useServiceUsers() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listByCustomer = useCallback(async (customerId: string): Promise<ServiceUserDto[]> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.GET('/api/v1/crm/orders/service-users/customer/{customerId}', {
        params: { path: { customerId } },
      });
      if (apiErr) throw new ApiError(apiErr);
      return data as ServiceUserDto[];
    } catch (err) {
      const msg = friendlyOrderErrorMessage(err, 'Failed to list service users');
      setError(msg);
      throw new OrderHookError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const createServiceUser = useCallback(async (dto: CreateServiceUserDto): Promise<ServiceUserDto> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr } = await apiClient.POST('/api/v1/crm/orders/service-users', {
        body: dto,
      });
      if (apiErr) throw new ApiError(apiErr);
      return data as ServiceUserDto;
    } catch (err) {
      const msg = friendlyOrderErrorMessage(err, 'Failed to create service user');
      setError(msg);
      throw new OrderHookError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    listByCustomer,
    createServiceUser,
  };
}
